import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { decodeJwt } from '../utils/jwt';
import { ACTIVE_ORG_STORAGE_KEY } from '../utils/orgRoutes';

// Auth0 custom claims namespace
const CLAIMS_NAMESPACE = 'https://antioch.com/';

export interface Organization {
  id: string;
  name: string;
  display_name: string;
}

export interface OrgClaims {
  orgId: string | null;
  orgName: string | null;
  userId: string | null;
  organizations: Organization[] | null;
}

interface UseOrgClaimsResult extends OrgClaims {
  isLoading: boolean;
  error: string | null;
  refreshClaims: () => Promise<void>;
}

/**
 * Hook to extract organization claims from the Auth0 access token.
 * Claims are added by the Auth0 action with namespace https://antioch.com/
 */
export function useOrgClaims(): UseOrgClaimsResult {
  const { getAccessTokenSilently, isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const [claims, setClaims] = useState<OrgClaims>({
    orgId: null,
    orgName: null,
    userId: null,
    organizations: null,
  });
  const [isFetchingClaims, setIsFetchingClaims] = useState(false);
  const [hasFetchedClaims, setHasFetchedClaims] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClaims = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsFetchingClaims(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const payload = decodeJwt(accessToken);

      if (!payload) {
        setError('Failed to decode access token');
        return;
      }

      // Extract custom claims from the namespace
      const orgId = payload[`${CLAIMS_NAMESPACE}org_id`] as string | undefined;
      const orgName = payload[`${CLAIMS_NAMESPACE}org_name`] as string | undefined;
      const userId = payload[`${CLAIMS_NAMESPACE}user_id`] as string | undefined;
      const organizations = payload[`${CLAIMS_NAMESPACE}organizations`] as Organization[] | undefined;

      // Persist the token's org so AuthProvider can pin it into authorizationParams
      // on the next bootstrap, keeping silent refreshes in-org even from an
      // org-less landing URL (ENG-1217). This is the validated current-token org.
      if (orgId) {
        try {
          window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
        } catch {
          // localStorage unavailable (private mode / blocked) -- non-fatal.
        }
      }

      setClaims({
        orgId: orgId ?? null,
        orgName: orgName ?? null,
        userId: userId ?? null,
        organizations: organizations ?? null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      console.error('[useOrgClaims] Error extracting claims:', err);
    } finally {
      setIsFetchingClaims(false);
      setHasFetchedClaims(true);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Reset fetch state when auth status changes so claims are re-fetched on re-login
  useEffect(() => {
    if (!isAuthenticated) {
      setHasFetchedClaims(false);
    }
  }, [isAuthenticated]);

  // Fetch claims when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedClaims && !isFetchingClaims) {
      fetchClaims();
    }
  }, [isAuthenticated, hasFetchedClaims, isFetchingClaims, fetchClaims]);

  // Re-decode when the tab regains focus. The access token can be silently
  // rotated in the background (token expiry -> refresh), so claims cached at
  // mount can otherwise drift out of sync with the token the API actually uses.
  // getAccessTokenSilently returns the cached token without a network round-trip
  // unless it has expired, so this is cheap. Keeps the org label honest even if
  // a refresh ever changes the active org (defense in depth for ENG-1217).
  useEffect(() => {
    if (!isAuthenticated) return;
    const revalidate = () => {
      if (document.visibilityState === 'visible') void fetchClaims();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [isAuthenticated, fetchClaims]);

  // isLoading is true if Auth0 is still loading OR we're authenticated but haven't fetched claims yet
  const isLoading = isAuth0Loading || (isAuthenticated && !hasFetchedClaims);

  return {
    ...claims,
    isLoading,
    error,
    refreshClaims: fetchClaims,
  };
}
